import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Chip,
  useTheme
} from '@mui/material';
import {
  Business as BusinessIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import tokenManager from '../../services/tokenManager';
import { companiesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const CompanySwitcher = ({ onSwitch, noRedirect = false }) => {
  const theme = useTheme();
  const { user, updateUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentCompany, setCurrentCompany] = useState(null);

  const { data: companiesData, isLoading } = useQuery(
    ['companies'],
    async () => {
      // Do not pass all=true — that is a super-admin-only flag that bypasses
      // tenant isolation on the backend. Regular users should only see
      // companies belonging to their own tenant.
      const response = await companiesAPI.getAll();
      return response.data?.data || response.data;
    },
    { staleTime: 5 * 60 * 1000 } // Cache for 5 minutes
  );

  useQuery(
    ['companies', 'current'],
    async () => {
      const response = await companiesAPI.getCurrent();
      return response.data?.company || response.data?.data || response.data;
    },
    {
      onSuccess: (data) => {
        setCurrentCompany(data);
      }
    }
  );

  const switchCompanyMutation = useMutation(
    async (companyId) => {
      const response = await companiesAPI.switch(companyId);
      return response.data;
    },
    {
      onSuccess: (response) => {
        const { token, company } = response.data || response;
        if (token) {
          tokenManager.set(token);
          // Purge any stale token from localStorage (SEC-004 compliance)
          localStorage.removeItem('token');
          
          if (updateUser && user) {
            updateUser({ ...user, companyId: company?.id === 'all' ? null : (company?.id || null) });
          }

          enqueueSnackbar(`Switched to ${company?.name || 'new context'}`, { variant: 'success' });
          handleClose();
          
          if (onSwitch) {
            onSwitch(company);
          }

          if (!noRedirect) {
            // Hard refresh to reload all app state with new company context
            setTimeout(() => {
              window.location.href = '/dashboard';
            }, 500);
          } else {
            // If no redirect, we should at least invalidate the current company query
            queryClient.invalidateQueries(['companies', 'current']);
          }
        }
      },
      onError: (error) => {
        enqueueSnackbar('Failed to switch company', { variant: 'error' });
      }
    }
  );

  const companies = Array.isArray(companiesData?.items)
    ? companiesData.items
    : Array.isArray(companiesData)
      ? companiesData
      : [];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSwitchCompany = (companyId) => {
    if (companyId === currentCompany?.id) {
      handleClose();
      return;
    }
    switchCompanyMutation.mutate(companyId);
  };

  if (isLoading || !currentCompany) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <>
      <Button
        onClick={handleClick}
        startIcon={<BusinessIcon />}
        endIcon={<ArrowDownIcon />}
        sx={{
          color: 'inherit',
          textTransform: 'none',
          px: 2,
          py: 1,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <Box sx={{ textAlign: 'left', ml: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'inherit' }}>
            {currentCompany?.name || 'Select Company'}
          </Typography>
          {currentCompany?.code && currentCompany?.code !== 'ALL' && (
            <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1, color: 'inherit' }}>
              {currentCompany.code}
            </Typography>
          )}
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxHeight: 400,
            mt: 1,
            boxShadow: 3,
            borderRadius: 2
          }
        }}
        transformOrigin={{ horizontal: theme.direction === 'rtl' ? 'left' : 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: theme.direction === 'rtl' ? 'left' : 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.50' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            SWITCH CONTEXT
          </Typography>
        </Box>
        <Divider />

        {/* All Companies Option */}
        <MenuItem
          onClick={() => handleSwitchCompany('all')}
          selected={!currentCompany?.id || currentCompany?.id === 'all' || currentCompany?.name === 'All Companies'}
          sx={{
            py: 1.5,
            px: 2,
            '&.Mui-selected': {
              backgroundColor: 'rgba(25, 118, 210, 0.12)',
              borderInlineStart: '3px solid',
              borderColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.18)'
              }
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            {(!currentCompany?.id || currentCompany?.id === 'all' || currentCompany?.name === 'All Companies') ? (
              <CheckIcon fontSize="small" color="primary" />
            ) : (
              <BusinessIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  All Companies
                </Typography>
                {(!currentCompany?.id || currentCompany?.id === 'all' || currentCompany?.name === 'All Companies') && (
                  <Chip label="Global" size="small" color="primary" sx={{ height: 20 }} />
                )}
              </Box>
            }
            secondary="View data across all organizations"
          />
        </MenuItem>
        <Divider />

        {companies.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No specific companies available
            </Typography>
          </MenuItem>
        ) : (
          companies.map((company) => (
            <MenuItem
              key={company.id}
              onClick={() => handleSwitchCompany(company.id)}
              selected={company.id === currentCompany?.id}
              sx={{
                py: 1.5,
                px: 2,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  borderLeft: '3px solid',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.18)'
                  }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {company.id === currentCompany?.id ? (
                  <CheckIcon fontSize="small" color="primary" />
                ) : (
                  <BusinessIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {company.name}
                    </Typography>
                    {company.id === currentCompany?.id && (
                      <Chip label="Active" size="small" color="primary" sx={{ height: 20 }} />
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {company.code} {company.currency ? `• ${company.currency}` : ''}
                  </Typography>
                }
              />
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default CompanySwitcher;
